import React, { useEffect, useState, useMemo } from 'react';
import { apiClient } from '../../App';
import Layout from '../../components/Layout';
import { Loader2, Calendar, Trophy, Users, CheckCircle2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * Admin "Daily Points Matrix"
 * Rows = users, Columns = day 1..28 of the selected month.
 * Each cell shows the total points that user earned that day.
 * Hover reveals the specific actions they logged.
 *
 * Two tabs: "Current cycle" (default) and "Previous cycle".
 * Previous cycle is only meaningful until data for the next month arrives —
 * matches the coach's stated needs (see PRD Aug 2026 session).
 */
const AdminCheckinsMatrix = () => {
  // Compute default = current month, and "previous cycle" = one calendar month back
  const now = new Date();
  const [selected, setSelected] = useState({
    year: now.getFullYear(),
    month: now.getMonth() + 1, // 1-based
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const previousCycle = useMemo(() => {
    const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  }, [now]);

  const currentCycle = useMemo(() => ({
    year: now.getFullYear(),
    month: now.getMonth() + 1,
  }), [now]);

  const isCurrent = selected.year === currentCycle.year && selected.month === currentCycle.month;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await apiClient.get(
          `/admin/checkins-matrix?year=${selected.year}&month=${selected.month}`
        );
        if (!cancelled) setData(res.data);
      } catch (err) {
        console.error('Failed to fetch matrix:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selected]);

  const monthLabel = `${MONTH_NAMES[selected.month - 1]} ${selected.year}`;

  return (
    <Layout>
      <div className="min-h-screen bg-[#F5F4F1] text-stone-900 p-4 sm:p-6">
        <div className="max-w-[1600px] mx-auto">
          <header className="mb-5">
            <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
              <Calendar className="text-[#EFB342]" size={26} />
              Daily Points Matrix
            </h1>
            <p className="text-stone-600 mt-1 text-sm">
              Every active user&apos;s daily point log for the {isCurrent ? 'current' : 'previous'} 28-day challenge cycle. Hover any cell to see which specific actions were logged that day.
            </p>
          </header>

          {/* Cycle tabs */}
          <div className="flex gap-2 mb-5" data-testid="cycle-tabs">
            <Button
              variant={isCurrent ? 'default' : 'outline'}
              className={isCurrent ? 'bg-[#EFB342] text-black hover:bg-[#D99F30]' : 'border-stone-300 text-stone-700'}
              onClick={() => setSelected(currentCycle)}
              data-testid="tab-current-cycle"
            >
              Current cycle · {MONTH_NAMES[currentCycle.month - 1]}
            </Button>
            <Button
              variant={!isCurrent ? 'default' : 'outline'}
              className={!isCurrent ? 'bg-[#EFB342] text-black hover:bg-[#D99F30]' : 'border-stone-300 text-stone-700'}
              onClick={() => setSelected(previousCycle)}
              data-testid="tab-previous-cycle"
            >
              Previous cycle · {MONTH_NAMES[previousCycle.month - 1]}
            </Button>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-24 text-stone-500">
              <Loader2 className="animate-spin mr-2" size={20} /> Loading matrix for {monthLabel}…
            </div>
          )}

          {!loading && data && <MatrixTable data={data} />}
        </div>
      </div>
    </Layout>
  );
};

/**
 * Detail modal — shows exactly which points a user earned on a given day.
 * Lives at the parent Table level so the sticky first column doesn't clip it.
 */
const CellDetailModal = ({ open, onClose, detail, actionsMap }) => {
  if (!detail) return null;
  const { user, date, cell } = detail;
  const points = cell?.total_points || 0;
  const ids = cell?.action_ids || [];
  const prettyDate = date && new Date(date + 'T12:00:00').toLocaleDateString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="bg-white border-stone-200 text-stone-900 max-w-md"
        data-testid="cell-detail-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-xl">
            {user?.name || '(no name)'}
          </DialogTitle>
          <p className="text-xs text-stone-500 pt-1">{user?.email}</p>
        </DialogHeader>

        <div className="mt-2">
          <p className="text-sm text-stone-600">{prettyDate}</p>
          <p className="text-3xl font-bold text-[#EFB342] mt-1" data-testid="cell-detail-points">
            {points} <span className="text-sm text-stone-500 font-normal">point{points === 1 ? '' : 's'}</span>
          </p>
        </div>

        {ids.length > 0 ? (
          <div className="mt-4">
            <p className="text-xs uppercase tracking-wide text-stone-500 mb-2">Actions logged</p>
            <ul className="space-y-2" data-testid="cell-detail-actions">
              {ids.map((aid) => (
                <li
                  key={aid}
                  className="flex items-center gap-2 bg-stone-50/60 border border-stone-200 rounded-lg px-3 py-2"
                >
                  <CheckCircle2 size={16} className="text-emerald-400 shrink-0" />
                  <span className="text-sm">{actionsMap[aid]?.name || aid}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-sm text-stone-500 mt-4">No check-in recorded on this day.</p>
        )}
      </DialogContent>
    </Dialog>
  );
};

const MatrixTable = ({ data }) => {
  const { days = [], users = [], actions_map = {} } = data;
  const [detail, setDetail] = useState(null);
  const totalActive = users.filter(u => u.days_checked_in > 0).length;
  const totalCheckins = users.reduce((s, u) => s + u.days_checked_in, 0);

  const handleCellClick = (user, date, cell) => {
    // Only open modal when there's actually a check-in — blank cells are noise
    if (!cell || (cell.total_points || 0) === 0) return;
    setDetail({ user, date, cell });
  };

  return (
    <>
      {/* Summary strip */}
      <div className="grid grid-cols-3 gap-3 mb-4" data-testid="matrix-summary">
        <StatCard icon={<Users size={16} />} label="Members shown" value={users.length} />
        <StatCard icon={<Trophy size={16} />} label="Members who checked in" value={totalActive} />
        <StatCard icon={<Calendar size={16} />} label="Total check-in days" value={totalCheckins} />
      </div>

      {/* Table */}
      <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="matrix-table">
            <thead className="bg-stone-50">
              <tr>
                <th className="sticky left-0 z-10 bg-stone-50 text-left px-3 py-2 border-b border-r border-stone-200 font-medium text-stone-600 min-w-[220px]">
                  Member
                </th>
                <th className="text-center px-2 py-2 border-b border-stone-200 font-medium text-stone-600 whitespace-nowrap">
                  Total
                </th>
                {days.map((d) => {
                  const day = parseInt(d.split('-')[2], 10);
                  return (
                    <th
                      key={d}
                      className="text-center px-1.5 py-2 border-b border-stone-200 font-medium text-stone-500 min-w-[36px]"
                    >
                      {day}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr>
                  <td colSpan={days.length + 2} className="text-center py-8 text-stone-500">
                    No users to display for this cycle.
                  </td>
                </tr>
              )}
              {users.map((u) => (
                <UserRow key={u.id} user={u} days={days} actionsMap={actions_map} onCellClick={handleCellClick} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-stone-500 mt-3">
        Sorted by total monthly points (highest first). Zero-point cells stay blank so pattern gaps are easy to spot. <strong className="text-stone-600">Click any filled cell</strong> to see exactly which actions were logged; hover shows a quick tooltip.
      </p>

      <CellDetailModal
        open={!!detail}
        onClose={() => setDetail(null)}
        detail={detail}
        actionsMap={actions_map}
      />
    </>
  );
};

const UserRow = ({ user, days, actionsMap, onCellClick }) => {
  return (
    <tr className="hover:bg-stone-50 transition-colors" data-testid={`matrix-row-${user.id}`}>
      <td className="sticky left-0 z-[5] bg-white hover:bg-stone-50 px-3 py-2 border-b border-r border-stone-200 min-w-[220px]">
        <div className="flex items-center gap-2">
          <div className="min-w-0">
            <p className="text-stone-900 font-medium truncate">
              {user.name}
              {user.is_coach && (
                <span className="ml-2 text-[10px] text-[#EFB342] uppercase tracking-wide">Coach</span>
              )}
              {user.user_type === 'trial' && (
                <span className="ml-2 text-[10px] text-sky-400 uppercase tracking-wide">Trial</span>
              )}
            </p>
            <p className="text-[11px] text-stone-500 truncate">{user.email}</p>
          </div>
        </div>
      </td>
      <td className="text-center px-2 py-2 border-b border-stone-200 font-semibold text-[#EFB342] whitespace-nowrap">
        {user.total_points}
      </td>
      {days.map((d) => {
        const cell = user.daily?.[d];
        return (
          <MatrixCell key={d} cell={cell} date={d} actionsMap={actionsMap} onClick={() => onCellClick(user, d, cell)} />
        );
      })}
    </tr>
  );
};

const MatrixCell = ({ cell, date, actionsMap, onClick }) => {
  const points = cell?.total_points || 0;
  const actionIds = cell?.action_ids || [];
  const actionNames = actionIds
    .map((aid) => actionsMap[aid]?.name || aid)
    .filter(Boolean);

  const tooltip = points > 0
    ? `${date}\n${points} point${points === 1 ? '' : 's'}\n\n${actionNames.map(n => `• ${n}`).join('\n') || '(no actions recorded)'}`
    : `${date}\nNo check-in`;

  const bgClass = points === 0
    ? 'bg-transparent'
    : points >= 8
      ? 'bg-emerald-500/25 text-emerald-200 border-emerald-500/40'
      : points >= 5
        ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/25'
        : points >= 3
          ? 'bg-[#EFB342]/15 text-[#B8871E] border-[#EFB342]/30'
          : 'bg-stone-100/60 text-stone-700 border-stone-300';

  return (
    <td
      className="text-center px-1 py-1.5 border-b border-stone-200/60"
      title={tooltip}
    >
      {points > 0 ? (
        <button
          type="button"
          onClick={onClick}
          className={`inline-flex items-center justify-center min-w-[28px] px-1.5 py-0.5 rounded border text-xs font-semibold cursor-pointer hover:brightness-125 hover:ring-2 hover:ring-[#EFB342]/50 focus:outline-none focus:ring-2 focus:ring-[#EFB342] transition ${bgClass}`}
          data-testid={`matrix-cell-${date}`}
          aria-label={`View ${points} point${points === 1 ? '' : 's'} on ${date}`}
        >
          {points}
        </button>
      ) : (
        <span className="text-stone-400">·</span>
      )}
    </td>
  );
};

const StatCard = ({ icon, label, value }) => (
  <div className="bg-white border border-stone-200 rounded-xl p-3 flex items-center gap-3">
    <div className="w-8 h-8 rounded-lg bg-[#EFB342]/15 text-[#EFB342] flex items-center justify-center">
      {icon}
    </div>
    <div>
      <p className="text-[11px] uppercase tracking-wide text-stone-500">{label}</p>
      <p className="text-lg font-semibold text-stone-900">{value}</p>
    </div>
  </div>
);

export default AdminCheckinsMatrix;
